import { useState, useCallback, useRef, useEffect } from 'react';

interface UseResizeProps {
  onResize: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
}

export const useResize = ({ 
  onResize, 
  minWidth = 350, 
  maxWidth = 600 
}: UseResizeProps) => {
  const [isResizing, setIsResizing] = useState(false);
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  const activeResizeRef = useRef<{
    startX: number;
    startWidth: number;
  } | null>(null);

  const handleResizeStart = useCallback((e: React.MouseEvent, currentWidth: number) => {
    e.preventDefault();
    setIsResizing(true);
    activeResizeRef.current = {
      startX: e.clientX,
      startWidth: currentWidth,
    };
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    let frameId: number | null = null;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!activeResizeRef.current) return;

      const { startX, startWidth } = activeResizeRef.current;
      const deltaX = startX - moveEvent.clientX; // Moving left increases width
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));

      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        onResizeRef.current(newWidth);
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      activeResizeRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minWidth, maxWidth]);

  return { isResizing, handleResizeStart };
};
